import {
  Component,
  Host,
  h,
  Prop,
  State,
  Listen,
  Event,
  EventEmitter,
} from '@stencil/core';
import {
  SessionDisplay,
  SessionRepository,
  SessionModel,
  SessionStayPeriod,
} from '@cesare';
import { getSessionFromSessionStorageOrEnv } from '../../core/app/app';

@Component({
  tag: 'qw-calendar',
  styleUrl: 'qw-calendar.scss',
  shadow: false,
})
export class QwCalendar {
  @Prop() qwCalendarId: string;
  @Prop() qwCalendarNumberOfMonths: number = 1;
  @Prop() qwCalendarResponsive: boolean = true;
  @Prop() qwCalendarSyncOnChange: boolean = true;
  @Prop() qwCalendarDesktopLimit: number = 700;
  @Prop() qwCalendarConfig: any; // flatpickr Options
  @Prop() qwLocationsList: any;
  @State() session: SessionModel;
  @State() stayPeriod: SessionStayPeriod;
  @State() isSessionLoading: boolean;
  @State() locale: SessionDisplay['culture'];
  @Event() qwCalendarChange: EventEmitter<SessionStayPeriod>;
  @Event() qwBasketWillBeReset: EventEmitter<void>;

  public componentWillLoad() {
    this.session = getSessionFromSessionStorageOrEnv();
    this.stayPeriod = {} as SessionStayPeriod;
    setTimeout(
      () => (this.stayPeriod = { ...this.session.context.stayPeriod }),
    );
    this.locale = this.session.display.culture;
    SessionRepository.sessionIsLoading$.subscribe(
      (isLoading) => (this.isSessionLoading = isLoading),
    );

    if (this.qwLocationsList) {
      this.qwLocationsList = JSON.parse(this.qwLocationsList);
    }
  }
  public componentDidLoad() {
    const selectedLoaction = this.qwLocationsList.hostel.find(
      (item) => item.is_current === true,
    );
    if (selectedLoaction.disable_stay_period != false) {
      const newPeriod = this.checkStayPeriodsOverlapOnInit(this.stayPeriod, {
        arrivalDate: selectedLoaction.disable_stay_period.from,
        departureDate: selectedLoaction.disable_stay_period.to,
      });

      if (newPeriod) {
        this.stayPeriod = newPeriod;
        this.qwCalendarChange.emit(this.stayPeriod);
      }
    }
  }

  public checkStayPeriodsOverlapOnInit(
    currentStayPeriod: SessionStayPeriod,
    disableStayPeriod: SessionStayPeriod,
  ): SessionStayPeriod {
    const today = new Date();

    const disableFrom = new Date(disableStayPeriod.arrivalDate);
    const disableTo = new Date(disableStayPeriod.departureDate);
    const currentFrom = new Date(currentStayPeriod.arrivalDate);
    const currentTo = new Date(currentStayPeriod.departureDate);

    const isOverlapping = disableFrom <= currentTo && currentFrom <= disableTo;

    let nextAvailableFrom: Date;
    let nextAvailableTo: Date;

    if (!isOverlapping) return;

    const dayBeforeDisable = new Date(disableFrom);
    dayBeforeDisable.setDate(dayBeforeDisable.getDate() - 1);

    if (dayBeforeDisable >= today) {
      nextAvailableFrom = new Date(dayBeforeDisable);
      nextAvailableTo = new Date(dayBeforeDisable);
    } else {
      nextAvailableFrom = new Date(disableTo);
      nextAvailableFrom.setDate(nextAvailableFrom.getDate() + 1);

      nextAvailableTo = new Date(nextAvailableFrom);
      nextAvailableTo.setDate(nextAvailableTo.getDate() + 1);
    }
    const formattedFromDate = nextAvailableFrom.toISOString().split('T')[0];
    const formattedToDate = nextAvailableTo.toISOString().split('T')[0];

    const nextAvailablePeriod = {
      arrivalDate: formattedFromDate,
      departureDate: formattedToDate,
    };

    return nextAvailablePeriod;
  }

  @Listen('qwCalendarPickerChangeDates', { target: 'window' })
  updateStayPeriod(event: CustomEvent<SessionStayPeriod>) {
    this.stayPeriod = { ...event.detail };
    this.qwCalendarChange.emit(this.stayPeriod);
    document.querySelectorAll('qw-calendar').forEach((cal) => {
      cal.classList.remove('qw-component--show');
    });

    if (!this.qwCalendarSyncOnChange) {
      return;
    }

    if (SessionRepository.sessionHasRoomsSync()) {
      this.qwBasketWillBeReset.emit();
    }
  }

  @Listen('qwCalendarPickerChangeDatesOpenCalendar', { target: 'window' })
  updateStayPeriodAndManageCalendar(
    event: CustomEvent<{
      stayPeriod: SessionStayPeriod;
      relativeCalendar?: HTMLElement;
      openCalendar: boolean;
    }>,
  ) {
    const newStayPeriod = event.detail.stayPeriod;
    const relativeCalendar = event.detail.relativeCalendar;
    const openCalendar = event.detail.openCalendar;
    this.stayPeriod = { ...newStayPeriod };
    this.qwCalendarChange.emit(this.stayPeriod);

    if (
      !relativeCalendar?.classList.contains('qw-component--show') &&
      openCalendar
    ) {
      relativeCalendar.classList.add('qw-component--show');
    }

    if (!this.qwCalendarSyncOnChange) {
      return;
    }

    if (SessionRepository.sessionHasRoomsSync()) {
      this.qwBasketWillBeReset.emit();
    }
  }

  render() {
    return (
      <Host>
        <qw-calendar-picker
          qwCalendarPickerId={this.qwCalendarId}
          qwCalendarPickerDesktopLimit={this.qwCalendarDesktopLimit}
          qwCalendarPickerResponsive={this.qwCalendarResponsive}
          qwCalendarPickerNumberOfMonths={this.qwCalendarNumberOfMonths}
          qwCalendarPickerDisabled={this.isSessionLoading}
          qwCalendarPickerLocations={
            this.qwLocationsList ? this.qwLocationsList : {}
          }
          qwCalendarPickerLocale={this.locale}
          qwCalendarPickerStayPeriod={this.stayPeriod}
          qwCalendarPickerConfig={
            this.qwCalendarConfig ? JSON.parse(this.qwCalendarConfig) : {}
          }
        />
      </Host>
    );
  }
}
